/**
 * Customer support tickets + tenant-managed FAQ (Location Service Engine ``customer_support``). Every export renders
 * nothing / returns null for tenants without the module, so SupportPage is unchanged for them.
 */
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronRight, Loader2, MessageSquarePlus, Send, Ticket } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useLse } from "../../lib/lse";

export const TICKET_CATEGORIES = [
    ["ORDER_ISSUE", "Problem with an order"], ["MISSING_ITEM", "Missing item"], ["DAMAGED_ITEM", "Damaged item"],
    ["WRONG_ITEM", "Wrong item"], ["DELIVERY_ISSUE", "Delivery issue"], ["REFUND_REQUEST", "Refund request"],
    ["PAYMENT", "Payment"], ["OTHER", "Something else"],
];
const CAT = Object.fromEntries(TICKET_CATEGORIES);
const STATUS_TONE = {
    OPEN: "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200",
    IN_PROGRESS: "bg-sky-100 text-sky-800 dark:bg-sky-950/50 dark:text-sky-200",
    RESOLVED: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200",
    CLOSED: "bg-muted text-muted-foreground",
};
export const statusLabel = (s) => (s || "").replace(/_/g, " ").toLowerCase();

/** The tenant's own FAQ when it has one (null → the page keeps its built-in questions). */
export function useTenantFaqs() {
    const { has } = useLse();
    const on = has("customer_support");
    const [faqs, setFaqs] = useState(null);
    useEffect(() => {
        if (!on) return;
        api.get("/lse/faqs").then(({ data }) => setFaqs((data?.faqs || []).length ? data.faqs : null)).catch(() => {});
    }, [on]);
    return faqs;
}

export function LseSupportSection() {
    const { has } = useLse();
    const on = has("customer_support");
    const [params] = useSearchParams();
    const orderId = params.get("order");
    const [tickets, setTickets] = useState(null);
    const [open, setOpen] = useState(!!orderId);
    const [form, setForm] = useState({ category: orderId ? "ORDER_ISSUE" : "OTHER", subject: "", message: "" });
    const [busy, setBusy] = useState(false);
    const load = () => api.get("/lse/tickets").then(({ data }) => setTickets(data.tickets || [])).catch(() => setTickets([]));
    useEffect(() => { if (on) load(); }, [on]);
    if (!on) return null;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/lse/tickets", { ...form, order_id: orderId || undefined });
            toast.success("Ticket raised — we'll get back to you soon");
            setForm({ category: "OTHER", subject: "", message: "" });
            setOpen(false);
            load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Please fill in a subject and message (3+ characters)");
        } finally { setBusy(false); }
    };

    return (
        <section className="mx-4 mt-5" data-testid="lse-support">
            <div className="flex items-center justify-between mb-2 px-1">
                <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">My tickets</p>
                <button type="button" data-testid="lse-new-ticket" onClick={() => setOpen((v) => !v)}
                        className="text-xs font-semibold text-primary inline-flex items-center gap-1 min-h-[36px]">
                    <MessageSquarePlus size={14} /> Raise a ticket
                </button>
            </div>
            {open && (
                <form onSubmit={submit} className="bg-card rounded-2xl border border-border p-4 mb-3 space-y-3" data-testid="lse-ticket-form">
                    {orderId && <p className="text-xs text-muted-foreground">About order <span className="font-semibold">{orderId.slice(0, 8)}</span></p>}
                    <label className="block">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">What's it about?</span>
                        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                                className="mt-1 w-full rounded-xl bg-muted px-3 py-2.5 text-sm" data-testid="lse-ticket-category">
                            {TICKET_CATEGORIES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Subject</span>
                        <input value={form.subject} maxLength={120} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                               className="mt-1 w-full rounded-xl bg-muted px-3 py-2.5 text-sm" data-testid="lse-ticket-subject" required minLength={3} />
                    </label>
                    <label className="block">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Tell us more</span>
                        <textarea value={form.message} maxLength={2000} rows={4} onChange={(e) => setForm({ ...form, message: e.target.value })}
                                  className="mt-1 w-full rounded-xl bg-muted px-3 py-2.5 text-sm" data-testid="lse-ticket-message" required minLength={3} />
                    </label>
                    <button type="submit" disabled={busy} data-testid="lse-ticket-submit"
                            className="w-full min-h-[44px] rounded-xl bg-primary text-primary-foreground font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
                        {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send
                    </button>
                </form>
            )}
            <div className="bg-card rounded-2xl border border-border divide-y divide-border overflow-hidden">
                {tickets === null && <p className="p-4 text-sm text-muted-foreground">Loading…</p>}
                {tickets?.length === 0 && <p className="p-4 text-sm text-muted-foreground" data-testid="lse-tickets-empty">No tickets yet. Raise one and we'll reply here.</p>}
                {(tickets || []).map((t) => (
                    <Link key={t.id} to={`/support/tickets/${t.id}`} data-testid={`lse-ticket-${t.id}`} className="flex items-center gap-3 px-4 py-3">
                        <Ticket size={18} className="text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold truncate">{t.subject}</p>
                            <p className="text-xs text-muted-foreground truncate">{CAT[t.category] || t.category}{t.order_code ? ` · ${t.order_code}` : ""}</p>
                        </div>
                        {t.unread_by_customer && <span className="w-2 h-2 rounded-full bg-primary" aria-label="New reply" />}
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_TONE[t.status] || ""}`}>{statusLabel(t.status)}</span>
                        <ChevronRight size={16} className="text-muted-foreground" />
                    </Link>
                ))}
            </div>
        </section>
    );
}

export function LseTicketPage() {
    const { id } = useParams();
    const { has } = useLse();
    const [t, setT] = useState(undefined);
    const [msg, setMsg] = useState("");
    const [busy, setBusy] = useState(false);
    const load = () => api.get(`/lse/tickets/${id}`).then(({ data }) => setT(data)).catch(() => setT(null));
    useEffect(() => { if (has("customer_support")) load(); }, [id]);   // eslint-disable-line react-hooks/exhaustive-deps
    if (!has("customer_support") || t === null) {
        return <div className="p-6 text-center text-sm text-muted-foreground" data-testid="lse-ticket-missing">This ticket isn't available. <Link className="text-primary font-semibold" to="/support">Back to support</Link></div>;
    }
    if (t === undefined) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-muted-foreground" /></div>;
    const send = async (e) => {
        e.preventDefault();
        if (!msg.trim()) return;
        setBusy(true);
        try { const { data } = await api.post(`/lse/tickets/${id}/reply`, { message: msg.trim() }); setT(data); setMsg(""); }
        catch (err) { toast.error(err?.response?.data?.detail || "Couldn't send"); } finally { setBusy(false); }
    };
    return (
        <div className="pb-32" data-testid="lse-ticket-page">
            <header className="px-4 pt-5 pb-4 bg-card border-b border-border flex items-center gap-3 sticky top-0 z-30">
                <Link to="/support" className="p-1 -ml-1" aria-label="Back"><ArrowLeft size={22} /></Link>
                <div className="min-w-0 flex-1">
                    <h1 className="text-base font-bold truncate">{t.subject}</h1>
                    <p className="text-xs text-muted-foreground">{CAT[t.category] || t.category}{t.order_code ? ` · ${t.order_code}` : ""} · {statusLabel(t.status)}</p>
                </div>
            </header>
            <ol className="p-4 space-y-3">
                {(t.messages || []).map((m) => (
                    <li key={m.id} className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.from === "CUSTOMER" ? "ml-auto bg-primary text-primary-foreground" : "bg-card border border-border"}`}>
                        <p className="whitespace-pre-wrap break-words">{m.message}</p>
                        <p className={`text-[10px] mt-1 ${m.from === "CUSTOMER" ? "opacity-80" : "text-muted-foreground"}`}>{m.from === "CUSTOMER" ? "You" : "Support"} · {new Date(m.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</p>
                    </li>
                ))}
            </ol>
            {t.status !== "CLOSED" ? (
                <form onSubmit={send} className="fixed bottom-0 inset-x-0 bg-card border-t border-border p-3 flex gap-2 mx-auto" style={{ maxWidth: "var(--shell-width)", paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}>
                    <input value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={2000} placeholder="Write a reply…" data-testid="lse-ticket-reply"
                           className="flex-1 rounded-xl bg-muted px-3 py-2.5 text-sm min-w-0" />
                    <button type="submit" disabled={busy || !msg.trim()} className="rounded-xl bg-primary text-primary-foreground px-4 min-h-[44px] disabled:opacity-50" aria-label="Send">
                        {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    </button>
                </form>
            ) : <p className="text-center text-xs text-muted-foreground">This ticket is closed.</p>}
        </div>
    );
}
